// ═══════════════════════════════════════════════════════════════════
//  useAtlasCapabilities — Tier-aware Atlas capability resolver.
// ═══════════════════════════════════════════════════════════════════
//
//  Wraps `usePlan()` and exposes the four Atlas capabilities
//  (`atlas`, `atlas-github-scanner`, `atlas-ai`,
//  `atlas-private-repos`) plus the doc/sheet caps as a single object
//  callsites can spread or destructure.
//
//  Centralized so:
//    - Atlas page can render upsell card if !enabled
//    - WorkspaceBrowser can early-reject manifests over the doc-cap
//    - URL scanner can be rendered tier-conditionally
//    - AI hooks can opt-out tier-conditionally
//
//  Returns stable references via useMemo so consumers don't re-render
//  on unrelated `usePlan()` updates.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { canUse, type PlanTier } from '@/lib/planTiers';
import { useSystemSwitches } from '@/hooks/useSystemSwitches';
import { IS_ATLAS_BUILD } from '@/lib/buildMode';

export interface AtlasCapabilities {
  /** Can render `/atlas` at all. False ⇒ show upsell card. */
  atlasEnabled: boolean;
  /** Can scan public GitHub repos in the URL dialog. */
  atlasGithubScanner: boolean;
  /** Can use AI hooks that take atlas context. */
  atlasAI: boolean;
  /** Can authenticate to private GitHub repos. */
  atlasPrivateRepos: boolean;
  /** Can render the whole-atlas mega-canvas view (`/atlas/canvas`). */
  atlasMegaCanvas: boolean;
  /** Can pin atlases for offline access (forces full sheet cache). */
  atlasOfflinePinning: boolean;
  /** Max documents per atlas. -1 = unlimited. 0 = blocked. */
  atlasMaxDocs: number;
  /** Max sheets per document inside an atlas. -1 = unlimited. 0 = blocked. */
  atlasMaxSheetsPerDoc: number;
  /** Lowest tier that unlocks `atlasEnabled` (for upsell-card copy). */
  atlasMinTier: PlanTier;
  /** Lowest tier that unlocks the GitHub scanner. */
  atlasGithubScannerMinTier: PlanTier;
  /** Lowest tier that unlocks AI hooks. */
  atlasAIMinTier: PlanTier;
  /** Lowest tier that unlocks private repos. */
  atlasPrivateReposMinTier: PlanTier;
  /** Lowest tier that unlocks mega-canvas. */
  atlasMegaCanvasMinTier: PlanTier;
  /** Lowest tier that unlocks offline pinning. */
  atlasOfflinePinningMinTier: PlanTier;
  /** Lowest tier that unlocks the Move-to-Flow-Weaver action. */
  moveAtlasToFlowWeaverMinTier: PlanTier;
  /** Current tier (for messaging). */
  currentTier: PlanTier;
}

export function useAtlasCapabilities(): AtlasCapabilities {
  const { tier, limits } = usePlan();
  const { switches } = useSystemSwitches();

  return useMemo(() => {
    // Defense in depth: build flag (compile-time SKU) AND runtime
    // admin kill-switch AND tier-gate all must be true for the
    // capability to be considered enabled.
    const systemOn = IS_ATLAS_BUILD && switches.atlas;
    const atlasGate = canUse(limits, 'atlas');
    const scannerGate = canUse(limits, 'atlas-github-scanner');
    const aiGate = canUse(limits, 'atlas-ai');
    const privateReposGate = canUse(limits, 'atlas-private-repos');
    const megaCanvasGate = canUse(limits, 'atlas-mega-canvas');
    const offlinePinningGate = canUse(limits, 'atlas-offline-pinning');

    return {
      atlasEnabled: systemOn && limits.atlasEnabled,
      atlasGithubScanner: systemOn && limits.atlasGithubScanner,
      atlasAI: systemOn && limits.atlasAI && switches.ai,
      atlasPrivateRepos: systemOn && limits.atlasPrivateRepos,
      atlasMegaCanvas: systemOn && limits.atlasMegaCanvas,
      atlasOfflinePinning: systemOn && limits.atlasOfflinePinning,
      atlasMaxDocs: limits.atlasMaxDocs,
      atlasMaxSheetsPerDoc: limits.atlasMaxSheetsPerDoc,
      atlasMinTier: (atlasGate.requiredTier ?? 'pro') as PlanTier,
      atlasGithubScannerMinTier: (scannerGate.requiredTier ?? 'max') as PlanTier,
      atlasAIMinTier: (aiGate.requiredTier ?? 'max') as PlanTier,
      atlasPrivateReposMinTier:
        (privateReposGate.requiredTier ?? 'enterprise') as PlanTier,
      atlasMegaCanvasMinTier: (megaCanvasGate.requiredTier ?? 'max') as PlanTier,
      atlasOfflinePinningMinTier:
        (offlinePinningGate.requiredTier ?? 'enterprise') as PlanTier,
      moveAtlasToFlowWeaverMinTier:
        (megaCanvasGate.requiredTier ?? 'max') as PlanTier,
      currentTier: tier,
    };
  }, [limits, tier, switches.atlas, switches.ai]);
}

/**
 * Pure helper used in non-React contexts (e.g. event handlers that
 * already have `limits` in scope) to check whether a manifest exceeds
 * the doc-cap. Returns null if OK, or an explanation string if over.
 */
export function checkAtlasDocCap(
  docCount: number,
  maxDocs: number,
  currentTier: PlanTier,
): string | null {
  if (maxDocs < 0) return null; // unlimited
  if (docCount <= maxDocs) return null;
  return (
    `Detta atlas innehåller ${docCount} dokument men din ` +
    `${currentTier}-plan tillåter max ${maxDocs}.`
  );
}
